import { Button, Tooltip } from '@heroui/react';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceAround,
  Copy,
  Trash2,
} from 'lucide-react';
import React, { FC } from 'react';

import { useAdsGeneratorStore } from '../../model/store';
import { AdElement } from '../../model/types';
import { alignElements, AlignMode, DistributeAxis, distributeElements } from '../../utils/arrange';
import { PanelSection } from '../common';

interface SelectionPropertiesProps {
  elements: AdElement[];
}

const ALIGN_ACTIONS: { mode: AlignMode; icon: typeof AlignStartVertical; label: string }[] = [
  { mode: 'left', icon: AlignStartVertical, label: 'Wyrównaj do lewej' },
  { mode: 'centerX', icon: AlignCenterVertical, label: 'Wyśrodkuj w poziomie' },
  { mode: 'right', icon: AlignEndVertical, label: 'Wyrównaj do prawej' },
  { mode: 'top', icon: AlignStartHorizontal, label: 'Wyrównaj do góry' },
  { mode: 'centerY', icon: AlignCenterHorizontal, label: 'Wyśrodkuj w pionie' },
  { mode: 'bottom', icon: AlignEndHorizontal, label: 'Wyrównaj do dołu' },
];

/**
 * Akcje na kilku zaznaczonych elementach. Świadomie NIE pokazujemy tu edycji
 * kroju czy koloru: zmiana „wszystkim naraz" nadpisywałaby ustawienia, których
 * użytkownik w tym momencie nie widzi.
 */
export const SelectionProperties: FC<SelectionPropertiesProps> = ({ elements }) => {
  const commit = useAdsGeneratorStore((state) => state.commit);
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);
  const duplicateElement = useAdsGeneratorStore((state) => state.duplicateElement);
  const removeElement = useAdsGeneratorStore((state) => state.removeElement);

  const applyPatches = (patches: ReturnType<typeof alignElements>): void => {
    if (patches.length === 0) return;
    commit();
    patches.forEach(({ id, ...patch }) => updateElement(id, patch, true));
  };

  const canDistribute = elements.length > 2;

  return (
    <PanelSection title={`Zaznaczono ${elements.length} elementy`}>
      <div className="flex flex-wrap gap-1">
        {ALIGN_ACTIONS.map(({ mode, icon: Icon, label }) => (
          <Tooltip key={mode} content={label} size="sm">
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="flat"
              aria-label={label}
              onPress={() => applyPatches(alignElements(elements, mode))}
            >
              <Icon size={16} />
            </Button>
          </Tooltip>
        ))}
      </div>

      <div className="flex flex-wrap gap-1">
        <Tooltip content={canDistribute ? 'Równe odstępy w poziomie' : 'Potrzebne co najmniej trzy elementy'} size="sm">
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            variant="flat"
            aria-label="Równe odstępy w poziomie"
            isDisabled={!canDistribute}
            onPress={() => applyPatches(distributeElements(elements, 'horizontal' as DistributeAxis))}
          >
            <AlignHorizontalSpaceAround size={16} />
          </Button>
        </Tooltip>
        <Tooltip content={canDistribute ? 'Równe odstępy w pionie' : 'Potrzebne co najmniej trzy elementy'} size="sm">
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            variant="flat"
            aria-label="Równe odstępy w pionie"
            isDisabled={!canDistribute}
            onPress={() => applyPatches(distributeElements(elements, 'vertical' as DistributeAxis))}
          >
            <AlignVerticalSpaceAround size={16} />
          </Button>
        </Tooltip>
      </div>

      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          radius="sm"
          variant="flat"
          startContent={<Copy size={14} />}
          onPress={() => elements.forEach((element) => duplicateElement(element.id))}
        >
          Duplikuj
        </Button>
        <Button
          size="sm"
          radius="sm"
          variant="light"
          color="danger"
          startContent={<Trash2 size={14} />}
          onPress={() => elements.forEach((element) => removeElement(element.id))}
        >
          Usuń
        </Button>
      </div>
    </PanelSection>
  );
};
